// src/pages/VerifyEmailPage.tsx
// Liquid Glass design
import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !code) {
      setStatus('error');
      setMessage('Заполните email и код');
      return;
    }
    if (code.length !== 6) {
      setStatus('error');
      setMessage('Код должен быть 6 цифр');
      return;
    }

    setIsSubmitting(true);
    setStatus('loading');
    setMessage('Проверяем код...');

    try {
      const res = await fetch(`${API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();

      if (!res.ok) {
        setStatus('error');
        setMessage(data.error || 'Ошибка подтверждения');
        return;
      }

      setStatus('success');
      setMessage('Email успешно подтверждён! Сейчас перенаправим на страницу входа...');
      setTimeout(() => { window.location.href = '/login'; }, 2000);
    } catch (err: any) {
      setStatus('error');
      setMessage('Ошибка сети: ' + (err?.message || 'Unknown'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setStatus('error');
      setMessage('Введите email');
      return;
    }
    setIsResending(true);
    setStatus('loading');
    setMessage('Отправляем новый код...');
    try {
      const res = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus('error');
        setMessage(data.error || 'Ошибка отправки');
        return;
      }
      setStatus('success');
      setMessage('Новый код отправлен на вашу почту!');
    } catch (err: any) {
      setStatus('error');
      setMessage('Ошибка сети');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Floating blur-пятна для liquid эффекта */}
      <div style={{...styles.blob, ...styles.blob1}} />
      <div style={{...styles.blob, ...styles.blob2}} />
      <div style={{...styles.blob, ...styles.blob3}} />

      <div style={styles.card}>
        {/* Логотип ламы */}
        <div style={styles.logoWrap}>
          <img src="/images/10.png" alt="HR LAMA" style={styles.logo} />
        </div>

        <h1 style={styles.title}>Подтверждение email</h1>
        <p style={styles.subtitle}>
          {searchParams.get('email')
            ? 'Мы отправили 6-значный код на вашу почту'
            : 'Введите email и 6-значный код из письма'}
        </p>

        <form onSubmit={handleVerify} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <div style={styles.inputWrap}>
              <Mail size={18} style={styles.inputIcon} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.ru"
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Код подтверждения</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={styles.codeInput}
            />
          </div>

          {message && (
            <div style={{
              ...styles.message,
              ...(status === 'success' ? styles.messageSuccess : {}),
              ...(status === 'error' ? styles.messageError : {}),
              ...(status === 'loading' ? styles.messageLoading : {}),
            }}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.button,
              ...(isSubmitting ? styles.buttonDisabled : {}),
            }}
          >
            {isSubmitting ? 'Проверяем...' : 'Подтвердить email'}
          </button>
        </form>

        <button
          onClick={handleResend}
          disabled={isResending}
          style={styles.resendBtn}
        >
          <RefreshCw size={16} style={{ marginRight: 8 }} />
          {isResending ? 'Отправка...' : 'Отправить код ещё раз'}
        </button>

        <Link to="/login" style={styles.backLink}>
          <ArrowLeft size={16} style={{ marginRight: 6 }} />
          Вернуться на страницу входа
        </Link>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    padding: 20,
    position: 'relative',
    overflow: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  blob: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(80px)',
    opacity: 0.6,
    pointerEvents: 'none',
  },
  blob1: {
    width: 400, height: 400,
    background: '#00d4ff',
    top: '-100px', left: '-100px',
  },
  blob2: {
    width: 350, height: 350,
    background: '#ff6b9d',
    bottom: '-80px', right: '-80px',
  },
  blob3: {
    width: 300, height: 300,
    background: '#a8edea',
    top: '40%', right: '20%',
    opacity: 0.4,
  },

  card: {
    position: 'relative',
    zIndex: 1,
    background: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    borderRadius: 28,
    padding: '40px 36px',
    maxWidth: 480,
    width: '100%',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255,255,255,0.6)',
    boxSizing: 'border-box',
  },

  logoWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 16,
  },
  logo: {
    width: 88,
    height: 88,
    objectFit: 'contain',
    filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.2))',
  },

  title: {
    fontSize: 26,
    fontWeight: 700,
    color: '#1a1a2e',
    textAlign: 'center',
    margin: '0 0 8px 0',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: 14,
    color: 'rgba(26, 26, 46, 0.7)',
    textAlign: 'center',
    margin: '0 0 28px 0',
    lineHeight: 1.5,
  },

  form: { display: 'flex', flexDirection: 'column', gap: 18 },

  field: { display: 'flex', flexDirection: 'column', gap: 8 },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(26, 26, 46, 0.75)',
    letterSpacing: '0.3px',
  },

  inputWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: 16,
    color: 'rgba(26, 26, 46, 0.5)',
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    padding: '14px 16px 14px 44px',
    fontSize: 15,
    border: '1px solid rgba(255, 255, 255, 0.6)',
    borderRadius: 14,
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(10px)',
    outline: 'none',
    color: '#1a1a2e',
    boxSizing: 'border-box',
    transition: 'all 0.25s ease',
  },
  codeInput: {
    width: '100%',
    padding: '16px',
    fontSize: 28,
    letterSpacing: 10,
    textAlign: 'center',
    fontFamily: '"SF Mono", Menlo, monospace',
    fontWeight: 600,
    border: '1px solid rgba(255, 255, 255, 0.6)',
    borderRadius: 14,
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(10px)',
    outline: 'none',
    color: '#1a1a2e',
    boxSizing: 'border-box',
    transition: 'all 0.25s ease',
  },

  message: {
    padding: '12px 16px',
    borderRadius: 14,
    fontSize: 14,
    lineHeight: 1.5,
    textAlign: 'center',
    backdropFilter: 'blur(10px)',
  },
  messageSuccess: {
    background: 'rgba(16, 185, 129, 0.2)',
    color: '#065f46',
    border: '1px solid rgba(16, 185, 129, 0.4)',
  },
  messageError: {
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#7f1d1d',
    border: '1px solid rgba(239, 68, 68, 0.4)',
  },
  messageLoading: {
    background: 'rgba(59, 130, 246, 0.2)',
    color: '#1e3a8a',
    border: '1px solid rgba(59, 130, 246, 0.4)',
  },

  button: {
    width: '100%',
    padding: '16px 24px',
    fontSize: 16,
    fontWeight: 700,
    color: '#fff',
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    border: 'none',
    borderRadius: 40,
    cursor: 'pointer',
    boxShadow: '0 10px 24px rgba(37, 117, 252, 0.35), inset 0 1px 0 rgba(255,255,255,0.3)',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    letterSpacing: '0.3px',
    marginTop: 4,
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },

  resendBtn: {
    width: '100%',
    marginTop: 14,
    padding: '12px 20px',
    fontSize: 14,
    fontWeight: 600,
    color: '#2575fc',
    background: 'rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(37, 117, 252, 0.3)',
    borderRadius: 40,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },

  backLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 24,
    color: 'rgba(26, 26, 46, 0.7)',
    fontSize: 14,
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
};

export default VerifyEmailPage;
